import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { I18nContext, I18nModel, I18nProvider } from '@bingads-webui/copilot-i18n';
import { DialogProps, makeStyles, tokens } from '@fluentui/react-components';
import {
  ArrowCounterclockwise16Regular,
  SoundWaveCircleRegular,
  TextAddTRegular,
} from '@fluentui/react-icons';
import {
  FetchAssets, TextAssetType, OnRequestAsset, ITextAsset, OnClearAsset, Tone, RequestType, CustomRenderer,
} from './types.d';
import { InlineCopilotUI, IInlineCopilotOption } from './InlineCopilotUI';
import { Rewrite } from './Rewrite';
import { FindSimilar } from './FindSimilar';
import { DifferentTone } from './DifferentTone';
import { latestPromise } from './utils';
import {
  COPILOT_TITLE, FIND_SIMILAR_OPTION_TEXTS, FIND_SIMILAR_TITLE_TEXT_MAP, REVISE_DIALOG_TITLE_TEXT_MAP, REVISE_OPTION_TEXT, TONES, TRY_DIFFERENT_TONE_OPTION_TEXT, TRY_DIFFERNT_TONE_TITLE_TEXT,
} from './staticTexts';

const useTextAssetSuggestionStyles = makeStyles({
  icon: {
    marginRight: tokens.spacingHorizontalSNudge,
    width: tokens.fontSizeBase400,
    height: tokens.fontSizeBase400,
  },
});

export interface ITextAssetCopilotUIProps {
  assetType: TextAssetType;
  onSelect: (param: { value: ITextAsset[] }, operationName: string) => void;
  onClose?: (dialogType: RequestType) => void;
  disableReason?: string;
  locale?: string;
  assets: ITextAsset[] | null;
  error?: string;
  onRequestAsset: OnRequestAsset;
  loading: boolean;
  onClearAsset: OnClearAsset;
  renderOptionText?: CustomRenderer;
  renderDialogTitle?: CustomRenderer;
  modalType?: DialogProps['modalType'];
  onMenuOpen?: (assetType: TextAssetType) => void;
  onOperationDialogOpen?: (assetType: TextAssetType, operationName: string) => void,
  menuButtonAriaLabel?: string;
  onToneSelected?: (tone: string) => void;
  isNewLogo?: boolean;
}

export const TextAssetCopilotUIInternal: React.FC<ITextAssetCopilotUIProps> = React.memo(({
  assetType: textAssetType,
  disableReason,
  locale,
  assets,
  error,
  loading,
  onRequestAsset,
  onClearAsset,
  onSelect,
  onClose,
  renderOptionText,
  renderDialogTitle,
  modalType,
  onMenuOpen,
  onOperationDialogOpen,
  menuButtonAriaLabel,
  onToneSelected,
  isNewLogo,
}) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const title = i18n.getString(COPILOT_TITLE);
  const styles = useTextAssetSuggestionStyles();
  const [rewriteDialogOpen, setRewriteDialogOpen] = useState(false);
  const [findSimilarDialogOpen, setFindSimilarDialogOpen] = useState(false);
  const [differentToneDialogOpen, setFindDifferentToneDialogOpen] = useState(false);

  const getCurrentOpenDialog = useCallback(() => {
    let operationName = '';
    if (rewriteDialogOpen) {
      operationName = 'Rewrite';
    } else if (findSimilarDialogOpen) {
      operationName = 'FindSimilar';
    } else if (differentToneDialogOpen) {
      operationName = 'DifferentTone';
    }

    return operationName;
  }, [rewriteDialogOpen, findSimilarDialogOpen, differentToneDialogOpen]);

  useEffect(() => {
    if (rewriteDialogOpen || findSimilarDialogOpen || differentToneDialogOpen) {
      const operationName = getCurrentOpenDialog();
      onOperationDialogOpen?.(textAssetType, operationName);
    }
  }, [
    differentToneDialogOpen,
    findSimilarDialogOpen,
    getCurrentOpenDialog,
    onOperationDialogOpen,
    rewriteDialogOpen,
    textAssetType,
  ]);

  const onRewriteDialogOpenChange = useCallback<NonNullable<DialogProps['onOpenChange']>>((_ev, { open }) => {
    setRewriteDialogOpen(open);
    if (!open) {
      onClearAsset();
      if (onClose) {
        onClose('rewrite');
      }
    }
  }, [onClearAsset, onClose]);
  const onFindSimilarOpenChange = useCallback<NonNullable<DialogProps['onOpenChange']>>((_ev, { open }) => {
    setFindSimilarDialogOpen(open);
    if (!open) {
      onClearAsset();
      if (onClose) {
        onClose('findSimilar');
      }
    }
  }, [onClearAsset, onClose]);
  const onFindDifferentToneOpenChange = useCallback<NonNullable<DialogProps['onOpenChange']>>((_ev, { open }) => {
    setFindDifferentToneDialogOpen(open);
    if (!open) {
      onClearAsset();
      if (onClose) {
        onClose('differentTone');
      }
    }
  }, [onClearAsset, onClose]);
  const onSelectWithCloseDialog = useCallback((selectedAssets: { value: ITextAsset[] }) => {
    if (onSelect) {
      const currentOpenDialog = getCurrentOpenDialog();
      onSelect(selectedAssets, currentOpenDialog);
    }
    setRewriteDialogOpen(false);
    setFindSimilarDialogOpen(false);
    setFindDifferentToneDialogOpen(false);
    onClearAsset();
  }, [getCurrentOpenDialog, onClearAsset, onSelect, textAssetType]);
  const finalRenderDialogTitle = useCallback<NonNullable<ITextAssetCopilotUIProps['renderDialogTitle']>>((assetType, requestType) => {
    if (renderDialogTitle) {
      return renderDialogTitle(assetType, requestType);
    }

    switch (requestType) {
      case 'rewrite':
        return i18n.getString(REVISE_DIALOG_TITLE_TEXT_MAP[assetType]);
      case 'differentTone':
        return i18n.getString(TRY_DIFFERNT_TONE_TITLE_TEXT);
      case 'findSimilar':
        return i18n.getString(FIND_SIMILAR_TITLE_TEXT_MAP[assetType]);
      default:
        return '';
    }
  }, [i18n, renderDialogTitle]);

  const finalRenderOptionText = useCallback<NonNullable<ITextAssetCopilotUIProps['renderOptionText']>>((assetType, requestType) => {
    if (renderOptionText) {
      return renderOptionText(assetType, requestType);
    }

    switch (requestType) {
      case 'rewrite':
        return i18n.getString(REVISE_OPTION_TEXT);
      case 'differentTone':
        return i18n.getString(TRY_DIFFERENT_TONE_OPTION_TEXT);
      case 'findSimilar':
        return i18n.getString(FIND_SIMILAR_OPTION_TEXTS[assetType]);
      default:
        return '';
    }
  }, [i18n, renderOptionText]);

  const inlineCopilotOptions: IInlineCopilotOption[] = useMemo(() => ([
    {
      menuItem: (
        <div className="find-similar-item">
          <TextAddTRegular className={styles.icon} />
          {finalRenderOptionText(textAssetType, 'findSimilar')}
        </div>
      ),
      onClick: () => {
        setFindSimilarDialogOpen(true);
      },
      key: 'findSimilar',
    },
    {
      menuItem: (
        <div className="different-tone-item">
          <SoundWaveCircleRegular className={styles.icon} />
          {finalRenderOptionText(textAssetType, 'differentTone')}
        </div>
      ),
      onClick: () => {
        setFindDifferentToneDialogOpen(true);
      },
      key: 'tryADifferentTone',
    },
    {
      menuItem: (
        <div className="rewrite-item">
          <ArrowCounterclockwise16Regular className={styles.icon} />
          {finalRenderOptionText(textAssetType, 'rewrite')}
        </div>
      ),
      onClick: () => {
        setRewriteDialogOpen(true);
      },
      key: 'rewrite',
    },
  ]), [finalRenderOptionText, styles.icon, textAssetType]);

  const onRequestAssetWithLogging = useCallback<OnRequestAsset>((params) => {
    onRequestAsset(params);
  }, [getCurrentOpenDialog, onRequestAsset]);

  const subCopilotProps = useMemo(() => ({
    assets,
    error,
    onRequestAsset: onRequestAssetWithLogging,
    loading,
    assetType: textAssetType,
    i18n,
    onSelect: onSelectWithCloseDialog,
    onClearAsset,
    closeButtonLabel: i18n.getString(_TL_('Close')),
    modalType,
    onToneSelected,
  }), [assets, error, onRequestAssetWithLogging, loading, textAssetType, i18n, onSelectWithCloseDialog, onClearAsset, modalType]);
  const tones = useMemo(() => Object.keys(TONES).map((key) => ({
    tone: key as Tone,
    text: i18n.getString(TONES[key as Tone]),
  })), [i18n]);

  const onInlineCopilotOpen = useCallback(() => {
    onMenuOpen?.(textAssetType);
  }, [onMenuOpen, textAssetType]);

  return (
    <I18nProvider locale={locale}>
      <div className="text-asset-copilot">
        <InlineCopilotUI
          title={title}
          options={inlineCopilotOptions}
          disableReason={disableReason}
          onInlineCopilotOpen={onInlineCopilotOpen}
          menuButtonAriaLabel={menuButtonAriaLabel}
        />
        <Rewrite
          {...subCopilotProps}
          title={finalRenderDialogTitle(textAssetType, 'rewrite')}
          open={rewriteDialogOpen}
          onOpenChange={onRewriteDialogOpenChange}
          isNewLogo={isNewLogo}
        />
        <FindSimilar
          {...subCopilotProps}
          title={finalRenderDialogTitle(textAssetType, 'findSimilar')}
          open={findSimilarDialogOpen}
          onOpenChange={onFindSimilarOpenChange}
          isNewLogo={isNewLogo}
        />
        <DifferentTone
          {...subCopilotProps}
          title={finalRenderDialogTitle(textAssetType, 'differentTone')}
          tones={tones}
          open={differentToneDialogOpen}
          onOpenChange={onFindDifferentToneOpenChange}
          isNewLogo={isNewLogo}
        />
      </div>
    </I18nProvider>
  );
});

export const TextAssetCopilotUI: React.FC<ITextAssetCopilotUIProps & { locale?: string }> = React.memo((props) => (
  <I18nProvider locale={props.locale}>
    <TextAssetCopilotUIInternal {...props} />
  </I18nProvider>
));

export interface ITextAssetCopilot extends Omit<ITextAssetCopilotUIProps, 'assets' | 'loading' | 'error' | 'onRequestAsset' | 'onClearAsset'> {
  currentAssetIndex: number;
  existingAssets: {
    shortHeadlines?: string[],
    longHeadlines?: string[],
    descriptions?: string[],
  }
  fetchAssets: FetchAssets;
  finalUrl: string;
  onSuggestionsRendered?: (finalUrl: string, params: any, assets: ITextAsset[]) => void;
  renderDialogTitle?: CustomRenderer;
  renderOptionText?: CustomRenderer;
}

export const TextAssetCopilot: React.FC<ITextAssetCopilot> = React.memo((props) => {
  const {
    fetchAssets, finalUrl, currentAssetIndex, existingAssets, locale, onSuggestionsRendered,
  } = props;
  const [assets, setAssets] = useState<ITextAsset[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const enhancedFetchAssets = useMemo(() => latestPromise(fetchAssets), [fetchAssets]);

  const onRequestAssets = useCallback<OnRequestAsset>((params) => {
    setLoading(true);
    enhancedFetchAssets({
      ...params, finalUrl, currentAssetIndex, existingAssets, locale,
    }).then((resp) => {
      onSuggestionsRendered?.(finalUrl, params, resp);
      setAssets(resp);
    }).catch((err) => {
      setError(err.message);
    }).finally(() => {
      setLoading(false);
    });
  }, [
    currentAssetIndex,
    enhancedFetchAssets,
    existingAssets,
    finalUrl,
    locale,
    onSuggestionsRendered,
  ]);

  const onClearAsset = useCallback<OnClearAsset>(() => {
    setAssets(null);
    setError(undefined);
    setLoading(false);
  }, []);

  return (
    <TextAssetCopilotUI
      {...props}
      assets={assets}
      loading={loading}
      error={error}
      onRequestAsset={onRequestAssets}
      onClearAsset={onClearAsset}
    />
  );
});
